<template>
  <DatePicker
    :modelValue="range"
    :inputId
    selectionMode="range"
    :manualInput="false"
    dateFormat="dd/mm/yy"
    :placeholder
    showButtonBar
    appendTo="body"
    @update:modelValue="range = rangeOf($event)"
    @date-select="$emit('select')"
    @clear-click="$emit('clear')"
  />
</template>

<script setup lang="ts">
import DatePicker from "primevue/datepicker";

import type { DateRange } from "@/components/common/dateRange";

/**
 * A day range picked from PrimeVue's DatePicker in range mode, with its button bar. The model is
 * `[from, to]`, `[from, null]` while the second click is pending, or `null` once cleared.
 *
 * `select` follows every day click, after the model has changed, so a caller that applies the range
 * there sees the new one; it fires on the first click of a range too. `clear` follows the button
 * bar's Clear, after the model is `null`.
 */
withDefaults(
  defineProps<{
    inputId?: string;
    placeholder?: string;
  }>(),
  {
    inputId: undefined,
    placeholder: undefined,
  }
);

defineEmits<{
  select: [];
  clear: [];
}>();

const range = defineModel<DateRange | null>({ required: true });

// PrimeVue types the model for every selection mode at once; in range mode it reports a pair.
function rangeOf(value: unknown): DateRange | null {
  if (!Array.isArray(value)) return null;
  const [from, to]: unknown[] = value;
  if (!(from instanceof Date)) return null;
  return [from, to instanceof Date ? to : null];
}
</script>
